import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMoleculeDrawing } from '@peruselab/api-client/moleculeDrawings';
import { Icon } from './Icon';
import { DrawingThumbnail } from './DrawingThumbnail';

interface DrawingViewerProps {
  readonly drawingId: string;
  readonly onBack: () => void;
  /** Adds the drawing's structure to the user's own paper (and returns to it). */
  readonly onAddToPaper?: (() => void) | undefined;
}

/**
 * #811 -- someone else's public drawing, opened from search: shown read-only (only its owner can
 * change it), with a way to add it to your own paper. Your own drawings open in the editor.
 */
export function DrawingViewer({ drawingId, onBack, onAddToPaper }: DrawingViewerProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getMoleculeDrawing(drawingId).then(
      (drawing) => !cancelled && setTitle(drawing.title),
      (failure: unknown) =>
        !cancelled && setError(failure instanceof Error ? failure.message : t('drawingViewer.loadError'))
    );
    return () => {
      cancelled = true;
    };
  }, [drawingId, t]);

  return (
    <section
      className="reaction-composer drawing-viewer"
      aria-labelledby="drawing-viewer-heading"
      data-testid="drawing-viewer"
    >
      <div className="reaction-composer-header">
        <h2 id="drawing-viewer-heading">{title?.trim() || t('drawingViewer.heading')}</h2>
        {onAddToPaper ? (
          <button
            type="button"
            className="icon-button"
            onClick={onAddToPaper}
            aria-label={t('drawingViewer.addToPaper')}
            title={t('drawingViewer.addToPaper')}
          >
            <Icon name="add" />
          </button>
        ) : null}
        <button
          type="button"
          className="icon-button"
          onClick={onBack}
          aria-label={t('illustrationComposer.backToEditor')}
          title={t('illustrationComposer.backToEditor')}
        >
          <Icon name="arrowBack" />
        </button>
      </div>
      <p className="drawing-viewer-note">{t('drawingViewer.notYours')}</p>
      {error ? (
        <p className="import-error" role="alert">
          {error}
        </p>
      ) : (
        <div className="drawing-viewer-canvas">
          <DrawingThumbnail drawingId={drawingId} size={480} />
        </div>
      )}
    </section>
  );
}
